import { useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

/** Pixels one arrow-key press moves the handle. */
const KEYBOARD_STEP = 16;

interface ResizeHandleProps {
  width: number;
  minWidth: number;
  maxWidth: number;
  onWidthChange: (width: number) => void;
  /** Accessible name, e.g. "Resize entity details". */
  label: string;
  sx?: SxProps<Theme>;
}

/**
 * Draggable divider on the left edge of a right-hand panel — dragging left
 * widens the panel, right narrows it, clamped to [minWidth, maxWidth]. Also
 * focusable, with the arrow keys nudging the width.
 */
export function ResizeHandle({ width, minWidth, maxWidth, onWidthChange, label, sx }: ResizeHandleProps) {
  const dragRef = useRef<{ x: number; width: number } | null>(null);
  const clamp = (w: number) => Math.min(maxWidth, Math.max(minWidth, Math.round(w)));

  // Pointer capture keeps move/up events on the handle while the pointer
  // wanders over the page, so no window listeners are needed.
  const handlePointerDown = (e: PointerEvent<HTMLElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, width };
  };
  const handlePointerMove = (e: PointerEvent<HTMLElement>) => {
    if (!dragRef.current) return;
    onWidthChange(clamp(dragRef.current.width + dragRef.current.x - e.clientX));
  };
  const handlePointerUp = () => {
    dragRef.current = null;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowLeft') onWidthChange(clamp(width + KEYBOARD_STEP));
    else if (e.key === 'ArrowRight') onWidthChange(clamp(width - KEYBOARD_STEP));
    else return;
    e.preventDefault();
  };

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
      sx={[
        {
          width: 6,
          flexShrink: 0,
          cursor: 'col-resize',
          touchAction: 'none',
          borderRadius: 1,
          outline: 'none',
          '&:hover, &:focus-visible': { bgcolor: 'primary.main', opacity: 0.4 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}
